import { ArrowUp, LoaderCircle, Paperclip, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

type ChatComposerProps = {
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  busy?: boolean;
  placeholderKey?: string;
  className?: string;
  controlPrefix?: string;
  plusIcon?: boolean;
};

export function LearningChatComposer({
  draft,
  onDraftChange,
  onSend,
  busy = false,
  placeholderKey = "learningRoute.chatPlaceholder",
  className = "",
  controlPrefix = "learning-chat",
  plusIcon = false,
}: ChatComposerProps) {
  const { t } = useTranslation();
  const AttachIcon = plusIcon ? Plus : Paperclip;

  return (
    <form
      className={`flex items-center gap-2 rounded-full border border-border bg-background px-3 py-2 shadow-sm ${className}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (!busy) onSend();
      }}
    >
      <button
        aria-label={t("learningRoute.chatAttach")}
        className="grid size-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
        data-control-id={`${controlPrefix}.attach`}
        type="button"
      >
        <AttachIcon aria-hidden="true" className="size-5" strokeWidth={1.7} />
      </button>
      <label className="sr-only" htmlFor={`${controlPrefix}-input`}>
        {t(placeholderKey)}
      </label>
      <input
        className="min-h-11 min-w-0 flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-disabled"
        data-control-id={`${controlPrefix}.input`}
        disabled={busy}
        id={`${controlPrefix}-input`}
        onChange={(event) => onDraftChange(event.target.value)}
        placeholder={t(placeholderKey)}
        value={draft}
      />
      <button
        aria-label={t("learningRoute.chatSend")}
        className="grid size-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-40"
        data-control-id={`${controlPrefix}.send`}
        disabled={busy || !draft.trim()}
        type="submit"
      >
        {busy ? (
          <LoaderCircle aria-hidden="true" className="size-5 animate-spin" strokeWidth={2} />
        ) : (
          <ArrowUp aria-hidden="true" className="size-5" strokeWidth={2.2} />
        )}
      </button>
    </form>
  );
}
